import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "status-bar-container" }
const _hoisted_2 = { class: "status-bar" }
const _hoisted_3 = ["title"]
const _hoisted_4 = {
  key: 0,
  class: "status-label"
}
const _hoisted_5 = ["title"]
const _hoisted_6 = {
  key: 0,
  class: "status-label"
}
const _hoisted_7 = ["title"]
const _hoisted_8 = {
  key: 0,
  class: "status-label"
}
const _hoisted_9 = { class: "status-legend mt-2" }
const _hoisted_10 = { class: "legend-item" }
const _hoisted_11 = { class: "legend-item" }
const _hoisted_12 = { class: "legend-item" }
const _hoisted_13 = {
  key: 0,
  class: "mt-3"
}
const _hoisted_14 = { class: "text-h6" }
const _hoisted_15 = { class: "text-h6" }
const _hoisted_16 = { class: "text-h6" }
const _hoisted_17 = { class: "text-h6" }
const _hoisted_18 = { class: "text-caption mb-2" }
const _hoisted_19 = { class: "timeline-container" }
const _hoisted_20 = ["title"]

import { computed } from 'vue';
import type { RequestStats } from '@/esri/services/TempoDataService';
import DurationHistogram from './DurationHistogram.vue';

interface Props {
  stats: RequestStats[] | null;
  showTimeline?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'RequestStatistics',
  props: {
    stats: {},
    showTimeline: { type: Boolean, default: true }
  },
  emits: ["clear"],
  setup(__props: any) {

const props = __props;



// Overall stats
const totalRequests = computed(() => props.stats?.length || 0);
const successfulRequests = computed(() => 
  props.stats?.filter(s => s.status === 'success').length || 0
);
const failedRequests = computed(() => totalRequests.value - successfulRequests.value);
const successRate = computed(() => 
  totalRequests.value > 0 
    ? Math.round((successfulRequests.value / totalRequests.value) * 100) 
    : 0
);
const failureRate = computed(() => 100 - successRate.value);
const retriedRequests = computed(() => 
  props.stats?.filter(s => s.retriedFrom503 === true).length || 0
);
const retriedPercent = computed(() => 
  totalRequests.value > 0 
    ? Math.round((retriedRequests.value / totalRequests.value) * 100) 
    : 0
);
const totalSamples = computed(() => 
  props.stats?.reduce((sum, s) => sum + s.sampleCount, 0) || 0
);

// Calculate unique locations from the stats
const uniqueLocations = computed(() => {
  if (!props.stats) return 0;
  // This is approximate since we don't have location data in stats
  // We can estimate based on sample count and requests
  return Math.round(totalSamples.value / Math.max(totalRequests.value, 1));
});

// Success rate color
const successRateColor = computed(() => {
  const rate = successRate.value;
  if (rate >= 95) return 'text-success';
  if (rate >= 85) return 'text-warning';
  return 'text-error';
});

// Error breakdown
const error503Count = computed(() => 
  props.stats?.filter(s => s.errorType === '503').length || 0
);
const error400Count = computed(() => 
  props.stats?.filter(s => s.errorType === '400').length || 0
);
const errorOtherCount = computed(() => 
  props.stats?.filter(s => s.errorType === 'other').length || 0
);

const error503Percent = computed(() => 
  totalRequests.value > 0 
    ? Math.round((error503Count.value / totalRequests.value) * 100) 
    : 0
);
const error400Percent = computed(() => 
  totalRequests.value > 0 
    ? Math.round((error400Count.value / totalRequests.value) * 100) 
    : 0
);
const errorOtherPercent = computed(() => 
  totalRequests.value > 0 
    ? Math.round((errorOtherCount.value / totalRequests.value) * 100) 
    : 0
);

// Get details of "other" errors with their messages
const otherErrorDetails = computed(() => {
  if (!props.stats) return [];
  
  const otherErrors = props.stats.filter(s => s.errorType === 'other');
  
  // Group by error message and count occurrences
  const errorMap = new Map<string, { type: string; message: string; count: number }>();
  
  otherErrors.forEach(stat => {
    const message = stat.errorMessage || 'Unknown error';
    const statusCode = stat.statusCode ? `HTTP ${stat.statusCode}` : 'Network Error';
    const key = `${statusCode}:${message}`;
    
    if (errorMap.has(key)) {
      errorMap.get(key)!.count++;
    } else {
      errorMap.set(key, {
        type: statusCode,
        message: message,
        count: 1
      });
    }
  });
  
  // Convert to array and sort by count (descending)
  return Array.from(errorMap.values()).sort((a, b) => b.count - a.count);
});

// Timing statistics
const minDuration = computed(() => {
  if (!props.stats || props.stats.length === 0) return 0;
  return Math.round(Math.min(...props.stats.map(s => s.duration)));
});

const maxDuration = computed(() => {
  if (!props.stats || props.stats.length === 0) return 0;
  return Math.round(Math.max(...props.stats.map(s => s.duration)));
});

const avgDuration = computed(() => {
  if (!props.stats || props.stats.length === 0) return 0;
  const sum = props.stats.reduce((acc, s) => acc + s.duration, 0);
  return Math.round(sum / props.stats.length);
});

const medianDuration = computed(() => {
  if (!props.stats || props.stats.length === 0) return 0;
  const sorted = [...props.stats].map(s => s.duration).sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return Math.round(
    sorted.length % 2 === 0 
      ? (sorted[mid - 1] + sorted[mid]) / 2 
      : sorted[mid]
  );
});

// Timeline helpers
function getStatusClass(stat: RequestStats): string {
  if (stat.status === 'success') return 'status-success';
  if (stat.errorType === '503') return 'status-503';
  if (stat.errorType === '400') return 'status-400';
  return 'status-error';
}

function getBarWidth(stat: RequestStats): number {
  if (maxDuration.value === 0) return 0;
  return (stat.duration / maxDuration.value) * 100;
}

function getTooltip(stat: RequestStats): string {
  const time = new Date(stat.timestamp).toLocaleTimeString();
  return `${time}: ${stat.status} - ${stat.duration.toFixed(0)}ms - ${stat.sampleCount} samples`;
}

return (_ctx: any,_cache: any) => {
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_list_item_title = _resolveComponent("v-list-item-title")!
  const _component_v_list_item_subtitle = _resolveComponent("v-list-item-subtitle")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_list = _resolveComponent("v-list")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_text = _resolveComponent("v-card-text")!

  return (_openBlock(), _createBlock(_component_v_card, { class: "request-statistics pa-4" }, {
    default: _withCtx(() => [
      _createVNode(_component_v_card_title, { class: "text-h6" }, {
        default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
          _createTextVNode("Request Statistics", -1)
        ]))]),
        _: 1
      }),
      (__props.stats && __props.stats.length > 0)
        ? (_openBlock(), _createBlock(_component_v_card_text, { key: 0 }, {
            default: _withCtx(() => [
              _createVNode(_component_v_card, {
                variant: "outlined",
                class: "pa-3 mb-4"
              }, {
                default: _withCtx(() => [
                  _cache[5] || (_cache[5] = _createElementVNode("h4", { class: "mb-2" }, "Request Status Overview", -1)),
                  _createElementVNode("div", _hoisted_1, [
                    _createElementVNode("div", _hoisted_2, [
                      (successRate.value > 0)
                        ? (_openBlock(), _createElementBlock("div", {
                            key: 0,
                            class: "status-segment success",
                            style: _normalizeStyle({ width: `${successRate.value}%` }),
                            title: `${successfulRequests.value} successful (${successRate.value}%)`
                          }, [
                            (successRate.value > 10)
                              ? (_openBlock(), _createElementBlock("span", _hoisted_4, _toDisplayString(successRate.value) + "%", 1))
                              : _createCommentVNode("", true)
                          ], 12, _hoisted_3))
                        : _createCommentVNode("", true),
                      (retriedPercent.value > 0)
                        ? (_openBlock(), _createElementBlock("div", {
                            key: 1,
                            class: "status-segment retried",
                            style: _normalizeStyle({ width: `${retriedPercent.value}%` }),
                            title: `${retriedRequests.value} retried from 503 (${retriedPercent.value}%)`
                          }, [
                            (retriedPercent.value > 10)
                              ? (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(retriedPercent.value) + "%", 1))
                              : _createCommentVNode("", true)
                          ], 12, _hoisted_5))
                        : _createCommentVNode("", true),
                      (failureRate.value > 0)
                        ? (_openBlock(), _createElementBlock("div", {
                            key: 2,
                            class: "status-segment failed",
                            style: _normalizeStyle({ width: `${failureRate.value}%` }),
                            title: `${failedRequests.value} failed (${failureRate.value}%)`
                          }, [
                            (failureRate.value > 10)
                              ? (_openBlock(), _createElementBlock("span", _hoisted_8, _toDisplayString(failureRate.value) + "%", 1))
                              : _createCommentVNode("", true)
                          ], 12, _hoisted_7))
                        : _createCommentVNode("", true)
                    ]),
                    _createElementVNode("div", _hoisted_9, [
                      _createElementVNode("span", _hoisted_10, [
                        _cache[2] || (_cache[2] = _createElementVNode("span", { class: "legend-color success" }, null, -1)),
                        _createTextVNode(" Success: " + _toDisplayString(successfulRequests.value - retriedRequests.value), 1)
                      ]),
                      _createElementVNode("span", _hoisted_11, [
                        _cache[3] || (_cache[3] = _createElementVNode("span", { class: "legend-color retried" }, null, -1)),
                        _createTextVNode(" Retried from 503: " + _toDisplayString(retriedRequests.value), 1)
                      ]),
                      _createElementVNode("span", _hoisted_12, [
                        _cache[4] || (_cache[4] = _createElementVNode("span", { class: "legend-color failed" }, null, -1)),
                        _createTextVNode(" Failed: " + _toDisplayString(failedRequests.value), 1)
                      ])
                    ])
                  ])
                ]),
                _: 1
              }),
              _createVNode(_component_v_row, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_col, {
                    cols: "12",
                    md: "6"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_card, {
                        variant: "outlined",
                        class: "pa-3"
                      }, {
                        default: _withCtx(() => [
                          _cache[12] || (_cache[12] = _createElementVNode("h4", null, "Overall Performance", -1)),
                          _createVNode(_component_v_list, { density: "compact" }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_list_item, null, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_list_item_title, null, {
                                    default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                                      _createTextVNode("Total Requests:", -1)
                                    ]))]),
                                    _: 1
                                  }),
                                  _createVNode(_component_v_list_item_subtitle, null, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(totalRequests.value), 1)
                                    ]),
                                    _: 1
                                  })
                                ]),
                                _: 1
                              }),
                              _createVNode(_component_v_list_item, null, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_list_item_title, null, {
                                    default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                                      _createTextVNode("Successful:", -1)
                                    ]))]),
                                    _: 1
                                  }),
                                  _createVNode(_component_v_list_item_subtitle, {
                                    class: _normalizeClass(successRateColor.value)
                                  }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(successfulRequests.value) + " (" + _toDisplayString(successRate.value) + "%) ", 1)
                                    ]),
                                    _: 1
                                  }, 8, ["class"])
                                ]),
                                _: 1
                              }),
                              _createVNode(_component_v_list_item, null, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_list_item_title, null, {
                                    default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                                      _createTextVNode("Failed:", -1)
                                    ]))]),
                                    _: 1
                                  }),
                                  _createVNode(_component_v_list_item_subtitle, { class: "text-error" }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(failedRequests.value) + " (" + _toDisplayString(failureRate.value) + "%) ", 1)
                                    ]),
                                    _: 1
                                  })
                                ]),
                                _: 1
                              }),
                              _createVNode(_component_v_list_item, null, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_list_item_title, null, {
                                    default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                                      _createTextVNode("Retried:", -1)
                                    ]))]),
                                    _: 1
                                  }),
                                  _createVNode(_component_v_list_item_subtitle, null, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(retriedRequests.value), 1)
                                    ]),
                                    _: 1
                                  })
                                ]),
                                _: 1
                              }),
                              _createVNode(_component_v_list_item, null, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_list_item_title, null, {
                                    default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                                      _createTextVNode("Total Samples:", -1)
                                    ]))]),
                                    _: 1
                                  }),
                                  _createVNode(_component_v_list_item_subtitle, null, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(totalSamples.value), 1)
                                    ]),
                                    _: 1
                                  })
                                ]),
                                _: 1
                              }),
                              _createVNode(_component_v_list_item, null, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_list_item_title, null, {
                                    default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                                      _createTextVNode("Unique Locations:", -1)
                                    ]))]),
                                    _: 1
                                  }),
                                  _createVNode(_component_v_list_item_subtitle, null, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(uniqueLocations.value), 1)
                                    ]),
                                    _: 1
                                  })
                                ]),
                                _: 1
                              })
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_col, {
                    cols: "12",
                    md: "6"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_card, {
                        variant: "outlined",
                        class: "pa-3"
                      }, {
                        default: _withCtx(() => [
                          _cache[17] || (_cache[17] = _createElementVNode("h4", null, "Error Breakdown", -1)),
                          _createVNode(_component_v_list, { density: "compact" }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_list_item, null, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_list_item_title, null, {
                                    default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                                      _createTextVNode("503 Errors (Service Unavailable):", -1)
                                    ]))]),
                                    _: 1
                                  }),
                                  _createVNode(_component_v_list_item_subtitle, { class: "text-warning" }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(error503Count.value) + " (" + _toDisplayString(error503Percent.value) + "%) ", 1)
                                    ]),
                                    _: 1
                                  })
                                ]),
                                _: 1
                              }),
                              _createVNode(_component_v_list_item, null, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_list_item_title, null, {
                                    default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                                      _createTextVNode("400 Errors (Bad Request):", -1)
                                    ]))]),
                                    _: 1
                                  }),
                                  _createVNode(_component_v_list_item_subtitle, { class: "text-error" }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(error400Count.value) + " (" + _toDisplayString(error400Percent.value) + "%) ", 1)
                                    ]),
                                    _: 1
                                  })
                                ]),
                                _: 1
                              }),
                              _createVNode(_component_v_list_item, null, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_list_item_title, null, {
                                    default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                                      _createTextVNode("Other Errors:", -1)
                                    ]))]),
                                    _: 1
                                  }),
                                  _createVNode(_component_v_list_item_subtitle, { class: "text-error" }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(errorOtherCount.value) + " (" + _toDisplayString(errorOtherPercent.value) + "%) ", 1)
                                    ]),
                                    _: 1
                                  })
                                ]),
                                _: 1
                              })
                            ]),
                            _: 1
                          }),
                          (otherErrorDetails.value.length > 0)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                                _createVNode(_component_v_divider, { class: "mb-2" }),
                                _cache[16] || (_cache[16] = _createElementVNode("h5", { class: "text-caption mb-1" }, "Other Error Details:", -1)),
                                _createVNode(_component_v_list, {
                                  density: "compact",
                                  class: "error-details-list"
                                }, {
                                  default: _withCtx(() => [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(otherErrorDetails.value, (errorDetail, index) => {
                                      return (_openBlock(), _createBlock(_component_v_list_item, {
                                        key: index,
                                        class: "text-caption"
                                      }, {
                                        default: _withCtx(() => [
                                          _createVNode(_component_v_list_item_title, { class: "text-wrap" }, {
                                            default: _withCtx(() => [
                                              _createElementVNode("strong", null, _toDisplayString(errorDetail.type) + ":", 1),
                                              _createTextVNode(" " + _toDisplayString(errorDetail.message), 1)
                                            ]),
                                            _: 2
                                          }, 1024),
                                          _createVNode(_component_v_list_item_subtitle, { class: "text-caption" }, {
                                            default: _withCtx(() => [
                                              _createTextVNode(" Count: " + _toDisplayString(errorDetail.count), 1)
                                            ]),
                                            _: 2
                                          }, 1024)
                                        ]),
                                        _: 2
                                      }, 1024))
                                    }), 128))
                                  ]),
                                  _: 1
                                })
                              ]))
                            : _createCommentVNode("", true)
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_col, { cols: "12" }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_card, {
                        variant: "outlined",
                        class: "pa-3"
                      }, {
                        default: _withCtx(() => [
                          _cache[22] || (_cache[22] = _createElementVNode("h4", null, "Timing Statistics (ms)", -1)),
                          _createVNode(_component_v_row, null, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_col, {
                                cols: "6",
                                sm: "3"
                              }, {
                                default: _withCtx(() => [
                                  _cache[18] || (_cache[18] = _createElementVNode("div", { class: "text-caption" }, "Min Duration", -1)),
                                  _createElementVNode("div", _hoisted_14, _toDisplayString(minDuration.value), 1)
                                ]),
                                _: 1
                              }),
                              _createVNode(_component_v_col, {
                                cols: "6",
                                sm: "3"
                              }, {
                                default: _withCtx(() => [
                                  _cache[19] || (_cache[19] = _createElementVNode("div", { class: "text-caption" }, "Max Duration", -1)),
                                  _createElementVNode("div", _hoisted_15, _toDisplayString(maxDuration.value), 1)
                                ]),
                                _: 1
                              }),
                              _createVNode(_component_v_col, {
                                cols: "6",
                                sm: "3"
                              }, {
                                default: _withCtx(() => [
                                  _cache[20] || (_cache[20] = _createElementVNode("div", { class: "text-caption" }, "Average Duration", -1)),
                                  _createElementVNode("div", _hoisted_16, _toDisplayString(avgDuration.value), 1)
                                ]),
                                _: 1
                              }),
                              _createVNode(_component_v_col, {
                                cols: "6",
                                sm: "3"
                              }, {
                                default: _withCtx(() => [
                                  _cache[21] || (_cache[21] = _createElementVNode("div", { class: "text-caption" }, "Median Duration", -1)),
                                  _createElementVNode("div", _hoisted_17, _toDisplayString(medianDuration.value), 1)
                                ]),
                                _: 1
                              })
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_col, { cols: "12" }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_card, {
                        variant: "outlined",
                        class: "pa-3"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(DurationHistogram, {
                            stats: __props.stats,
                            "bin-count": 10,
                            title: "Request Duration Distribution (ms)"
                          }, null, 8, ["stats"])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  (__props.showTimeline && __props.stats && __props.stats.length > 0 && __props.stats.length <= 200)
                    ? (_openBlock(), _createBlock(_component_v_col, {
                        key: 0,
                        cols: "12"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_card, {
                            variant: "outlined",
                            class: "pa-3"
                          }, {
                            default: _withCtx(() => [
                              _cache[23] || (_cache[23] = _createElementVNode("h4", null, "Request Timeline", -1)),
                              _createElementVNode("div", _hoisted_18, "Duration visualization (showing " + _toDisplayString(__props.stats.length) + " requests)", 1),
                              _createElementVNode("div", _hoisted_19, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.stats, (stat, index) => {
                                  return (_openBlock(), _createElementBlock("div", {
                                    key: index,
                                    class: _normalizeClass(['timeline-bar', getStatusClass(stat)]),
                                    style: _normalizeStyle({ width: getBarWidth(stat) + '%', minHeight: '4px' }),
                                    title: getTooltip(stat)
                                  }, null, 14, _hoisted_20))
                                }), 128))
                              ])
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  (__props.showTimeline && __props.stats && __props.stats.length > 200)
                    ? (_openBlock(), _createBlock(_component_v_col, {
                        key: 1,
                        cols: "12"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_card, {
                            variant: "outlined",
                            class: "pa-3"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_alert, {
                                type: "info",
                                variant: "tonal",
                                density: "compact"
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(" Timeline hidden: Too many requests (" + _toDisplayString(__props.stats.length) + "). Use the Time Range Status List below for detailed view. ", 1)
                                ]),
                                _: 1
                              })
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              }),
              _createVNode(_component_v_row, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_col, {
                    cols: "12",
                    class: "text-center"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_btn, {
                        color: "error",
                        variant: "outlined",
                        onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('clear')))
                      }, {
                        default: _withCtx(() => [...(_cache[24] || (_cache[24] = [
                          _createTextVNode(" Clear Statistics ", -1)
                        ]))]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          }))
        : (_openBlock(), _createBlock(_component_v_card_text, { key: 1 }, {
            default: _withCtx(() => [
              _createVNode(_component_v_alert, {
                type: "info",
                variant: "outlined"
              }, {
                default: _withCtx(() => [...(_cache[25] || (_cache[25] = [
                  _createTextVNode(" No request statistics available yet. Fetch some data to see statistics. ", -1)
                ]))]),
                _: 1
              })
            ]),
            _: 1
          }))
    ]),
    _: 1
  }))
}
}

})